/**
 * Online consultations (client side).
 *
 * Capture-first flow:
 *   draft_capture      -> started free; capture invite issued, nothing billed
 *   awaiting_purchase  -> photos taken, still free; doctor may be chosen
 *   awaiting_ai        -> PAID. analysis queued, token spent
 *   ai_preliminary     -> draft exists but is NOT shown by default; the client
 *                         must ask for it (`getConsultationReport(id, {draft:true})`)
 *   confirmed          -> the confirmed version replaces the draft and the
 *                         chat window opens for a limited period
 *
 * BACKEND: /user/consultations/*, /physicians
 */
import axiosInstance from './axiosInstance';

/**
 * RULE 4 — the platform physician directory the client chooses from AFTER
 * paying. Unavailable doctors come back too, flagged `available:false` with an
 * `unavailableReason`, so the list is never an empty dead end.
 */
export async function listPhysicians({ serviceId, clinicCode } = {}) {
  const params = {};
  if (serviceId) params.serviceId = serviceId;
  if (clinicCode) params.clinicCode = clinicCode;
  const res = await axiosInstance.get('/physicians', { params });
  return res.data.data.physicians;
}

/** RULE 4 — commit the choice. Issues the face-capture invite server-side. */
export async function choosePhysician(id, physicianPhone) {
  const res = await axiosInstance.post(
    `/user/consultations/${encodeURIComponent(id)}/physician`, { physicianPhone });
  return res.data.data.consultation;
}

/** All of the client's consultations, newest first. */
export async function listConsultations() {
  const res = await axiosInstance.get('/user/consultations');
  return res.data.data.consultations;
}

export async function getConsultation(id) {
  const res = await axiosInstance.get(`/user/consultations/${encodeURIComponent(id)}`);
  return res.data.data.consultation;
}

/**
 * reportStatus: 'awaiting_purchase' | 'processing' | 'reviewing' | 'preliminary'
 *             | 'confirmed' | 'failed'
 *
 * Defaults to 'reviewing' once the AI draft exists: the unreviewed version is
 * only returned when the client explicitly asks, so an unread symmetry figure
 * never lands on them by surprise.
 */
export async function getConsultationReport(id, { draft = false } = {}) {
  const res = await axiosInstance.get(
    `/user/consultations/${encodeURIComponent(id)}/report`,
    { params: draft ? { draft: 1 } : {} },
  );
  return res.data.data;
}

/** Start a consultation and capture BEFORE paying. Nothing is charged. */
export async function startConsultation(serviceId, clinicCode) {
  const body = { serviceId };
  if (clinicCode) body.clinicCode = clinicCode;
  const res = await axiosInstance.post('/user/consultations/start', body);
  return res.data.data.consultation;
}

/** "Choose for me" — assign by specialty match, then lightest load. */
export async function choosePhysicianForMe(id) {
  const res = await axiosInstance.post(
    `/user/consultations/${encodeURIComponent(id)}/physician/auto`);
  return res.data.data.consultation;
}

/**
 * The FREE preview: the landmarks detected on the client's own capture, and
 * what each agent reads off them. Runs no agent and spends no token.
 */
export async function getPreview(id) {
  const res = await axiosInstance.get(`/user/consultations/${encodeURIComponent(id)}/preview`);
  return res.data.data;
}

/** Choose the service (and therefore the agent) after the preview. */
export async function setConsultationService(id, serviceId) {
  const res = await axiosInstance.post(
    `/user/consultations/${encodeURIComponent(id)}/service`, { serviceId });
  return res.data.data.consultation;
}

/**
 * Settle the consultation and start the analysis.
 *
 * One call whatever is behind it: today it settles immediately (the gateway is
 * not wired yet); later it returns { paymentUrl } and the client redirects.
 * Keeping the shape stable means this call does not change when that happens.
 */
export async function payConsultation(id, gateway, discountCode) {
  // A code, never an amount. The server re-quotes it against the catalog price
  // and charges what IT computes, so a tampered client can only ever send a
  // code that does or doesn't exist.
  const body = {};
  if (gateway) body.gateway = gateway;
  if (discountCode) body.discountCode = discountCode;
  const res = await axiosInstance.post(
    `/user/consultations/${encodeURIComponent(id)}/pay`,
    body,
  );
  return res.data.data;
}

/** Public: the roster and the promise (SLA, chat window, retention). */
export async function getConsultationTerms() {
  const res = await axiosInstance.get('/public/consultation-terms');
  return res.data.data;
}

/**
 * Returns { chatState, chatOpensAt, chatExpiresAt, messages }.
 * READING is always allowed — including after the window closes, so the client
 * keeps their medical correspondence.
 */
export async function listMessages(id) {
  const res = await axiosInstance.get(`/user/consultations/${encodeURIComponent(id)}/messages`);
  return res.data.data;
}

export async function sendMessage(id, body) {
  const res = await axiosInstance.post(`/user/consultations/${encodeURIComponent(id)}/messages`, { body });
  return res.data.data.message;
}

/**
 * GET /user/consultations/:id/evidence — what each finding was drawn from.
 *
 * Returns `{ measures, frames, poseCount, agentKey }`: the agent's measure map
 * (which landmark points and lines produced each finding, and which poses fed
 * it) together with the client's own captured frames and their landmarks.
 *
 * Passed through untouched — the visual report draws whatever the bot
 * recorded, so a bot that changes its measures changes the page with nothing
 * to update here.
 */
export async function getEvidence(consultationId) {
  const res = await axiosInstance.get(
    `/user/consultations/${encodeURIComponent(consultationId)}/evidence`);
  return res.data.data ?? res.data;
}

export const consultationsApi = {
  startConsultation, getPreview, setConsultationService, payConsultation, listPhysicians, choosePhysician, choosePhysicianForMe,
  listConsultations, getConsultation, getConsultationReport,
  getConsultationTerms, listMessages, sendMessage,
  getEvidence,
};
export default consultationsApi;

